import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, Tag } from 'lucide-react';
import { dataService, isDemoMode } from '@/services/data-service';
import type { Category, DepreciationMethod, DepreciationFrequency } from '@/types/settings';

const depreciationMethods: DepreciationMethod[] = [
  'Straight Line',
  'Declining Balance',
  'Sum of Years Digits',
  'Units of Production',
];

const depreciationFrequencies: DepreciationFrequency[] = [
  'Monthly',
  'Quarterly',
  'Annually',
];

export default function CategoriesPage(): React.ReactElement {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadCategories() {
      try {
        setLoading(true);
        setError(null);
        const data = await dataService.getCategories();
        setCategories(data);
      } catch (err) {
        console.error('Failed to load categories:', err);
        setError('Failed to load categories. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadCategories();
  }, []);

  const [formData, setFormData] = useState<Partial<Category>>({
    code: '',
    description: '',
    defaultMethod: 'Straight Line',
    defaultPercentage: 0,
    defaultPeriodYears: 0,
    defaultFrequency: 'Monthly',
    userField1: '',
    userField2: '',
    userField3: '',
    doNotDepreciate: false,
  });

  const handleOpenDialog = (category?: Category) => {
    if (category) {
      setEditingCategory(category);
      setFormData(category);
    } else {
      setEditingCategory(null);
      setFormData({
        code: '',
        description: '',
        defaultMethod: 'Straight Line',
        defaultPercentage: 0,
        defaultPeriodYears: 0,
        defaultFrequency: 'Monthly',
        userField1: '',
        userField2: '',
        userField3: '',
        doNotDepreciate: false,
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingCategory) {
        if (isDemoMode()) {
          setCategories(
            categories.map((cat) =>
              cat.id === editingCategory.id
                ? { ...cat, ...formData, modifiedDate: new Date().toISOString() }
                : cat
            )
          );
        } else {
          const updated = await dataService.updateCategory(editingCategory.id, {
            ...formData,
            modifiedDate: new Date().toISOString(),
          } as Category);
          setCategories(categories.map((cat) => (cat.id === updated.id ? updated : cat)));
        }
      } else {
        const newCategory: Category = {
          id: String(categories.length + 1),
          code: formData.code || '',
          description: formData.description,
          defaultMethod: formData.defaultMethod || 'Straight Line',
          defaultPercentage: formData.defaultPercentage || 0,
          defaultPeriodYears: formData.defaultPeriodYears || 0,
          defaultFrequency: formData.defaultFrequency || 'Monthly',
          userField1: formData.userField1,
          userField2: formData.userField2,
          userField3: formData.userField3,
          doNotDepreciate: formData.doNotDepreciate || false,
          assetCount: 0,
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
        };
        
        if (isDemoMode()) {
          setCategories([...categories, newCategory]);
        } else {
          const created = await dataService.createCategory(newCategory);
          setCategories([...categories, created]);
        }
      }
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Failed to save category:', err);
      alert('Failed to save category. Please try again.');
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this category?')) {
      try {
        if (isDemoMode()) {
          setCategories(categories.filter((cat) => cat.id !== id));
        } else {
          await dataService.deleteCategory(id);
          setCategories(categories.filter((cat) => cat.id !== id));
        }
      } catch (err) {
        console.error('Failed to delete category:', err);
        alert('Failed to delete category. Please try again.');
      }
    }
  };

  const filteredCategories = categories.filter((category) =>
    category.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Tag className="h-6 w-6 text-primary" />
              Asset Categories
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Define and organize asset categories and subcategories
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Add Category
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6 space-y-6">

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Method</TableHead>
              <TableHead className="text-right">Rate %</TableHead>
              <TableHead className="text-right">Period (Years)</TableHead>
              <TableHead>Frequency</TableHead>
              <TableHead className="text-center">Assets</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  Loading categories...
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-destructive">
                  {error}
                </TableCell>
              </TableRow>
            ) : filteredCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground">
                  No categories found
                </TableCell>
              </TableRow>
            ) : (
              filteredCategories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <Tag className="h-4 w-4 text-muted-foreground" />
                      {category.code}
                      {category.doNotDepreciate && (
                        <Badge variant="outline" className="ml-2">
                          No Depreciation
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{category.description || '-'}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{category.defaultMethod}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {category.defaultPercentage.toFixed(3)}
                  </TableCell>
                  <TableCell className="text-right">
                    {category.defaultPeriodYears.toFixed(3)}
                  </TableCell>
                  <TableCell>{category.defaultFrequency}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{category.assetCount}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(category)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(category.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : 'Add Category'}
            </DialogTitle>
            <DialogDescription>
              {editingCategory
                ? 'Update the category details below.'
                : 'Enter the details for the new category.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="code">Category Code *</Label>
                  <Input
                    id="code"
                    value={formData.code}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, code: e.target.value.toUpperCase() })
                    }
                    placeholder="e.g., CAR PARTS"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="e.g., Automotive parts and components"
                  />
                </div>
              </div>
            </div>

            {/* Depreciation Defaults */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Depreciation Defaults</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="method">Default Method</Label>
                  <Select
                    value={formData.defaultMethod}
                    onValueChange={(value: string) =>
                      setFormData({ ...formData, defaultMethod: value as DepreciationMethod })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {depreciationMethods.map((method) => (
                        <SelectItem key={method} value={method}>
                          {method}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="frequency">Default Frequency</Label>
                  <Select
                    value={formData.defaultFrequency}
                    onValueChange={(value: string) =>
                      setFormData({ ...formData, defaultFrequency: value as DepreciationFrequency })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {depreciationFrequencies.map((freq) => (
                        <SelectItem key={freq} value={freq}>
                          {freq}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="percentage">Default Percentage</Label>
                  <Input
                    id="percentage"
                    type="number"
                    step="0.001"
                    value={formData.defaultPercentage}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, defaultPercentage: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="e.g., 20.000"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="period">Default Period (Years)</Label>
                  <Input
                    id="period"
                    type="number"
                    step="0.001"
                    value={formData.defaultPeriodYears}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, defaultPeriodYears: parseFloat(e.target.value) || 0 })
                    }
                    placeholder="e.g., 5.000"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="doNotDepreciate"
                  checked={formData.doNotDepreciate}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, doNotDepreciate: checked })
                  }
                />
                <Label htmlFor="doNotDepreciate" className="cursor-pointer">
                  Do Not Depreciate
                </Label>
              </div>
            </div>

            {/* User Fields */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Custom Fields</h3>
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="userField1">User Field 1</Label>
                  <Input
                    id="userField1"
                    value={formData.userField1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, userField1: e.target.value })
                    }
                    placeholder="Custom field 1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="userField2">User Field 2</Label>
                  <Input
                    id="userField2"
                    value={formData.userField2}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, userField2: e.target.value })
                    }
                    placeholder="Custom field 2"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="userField3">User Field 3</Label>
                  <Input
                    id="userField3"
                    value={formData.userField3}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                      setFormData({ ...formData, userField3: e.target.value })
                    }
                    placeholder="Custom field 3"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!formData.code}>
              {editingCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
